'use client';
// FormSelect Component with React Hook Form
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
} from '@mui/material';

import type { SelectOption } from '@/types';

interface FormSelectProps<T extends FieldValues> extends Omit<
  SelectProps,
  'name' | 'value' | 'onChange'
> {
  control: Control<T>;
  name: FieldPath<T>;
  options: SelectOption[];
  helperText?: string;
}

export const FormSelect = <T extends FieldValues>({
  control,
  name,
  label,
  options,
  helperText,
  fullWidth = true,
  ...props
}: FormSelectProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth={fullWidth} error={!!error}>
          {label && <InputLabel>{label}</InputLabel>}
          {/* @ts-expect-error - MUI v5 Select types mismatch with React 19/hook-form */}
          <Select {...field} {...props} label={label} value={field.value ?? ''}>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {(error?.message || helperText) && (
            <FormHelperText>{error?.message || helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

export default FormSelect;
